import React, { useState, useMemo } from "react";
import { Col, Row } from "reactstrap";
import { withTranslation } from "react-i18next";
import CustomTable from "../../components/Common/CustomTable";
import AddExpenseModal from "./AddExpenseModal";
import ExpenseDetailModal from "./ExpenseDetailModal";
import useFilter from "../../Hooks/useFilter";
import { DATE_FORMAT, formatDisplayDateOrDash } from "../../helpers/dateFormat";
import { getExpenseList, deleteExpense } from "../../helpers/API";
import useApi from "../../Hooks/useApi";
import { handleDelete } from "../../helpers/CommonAPICall";
import { MODAL_SIZES } from "../../components/ConfirmModal";

const List = ({ t }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [expensesFilters, setExpensesFilters] = useFilter({ sortField: "date", sort: "desc" });

  const [expensesData, setExpensesData] = useApi({
    callback: getExpenseList,
    payload: [expensesFilters],
    dependencies: [expensesFilters, showAddModal, refresh],
  });

  const closeExpenseModal = () => {
    setShowAddModal(false);
    setEditingExpenseId(null);
  };

  const openAddExpenseModal = () => {
    setEditingExpenseId(null);
    setShowAddModal(true);
  };

  const expensesColumns = useMemo(
    () => [
      {
        name: t("expenses.list.columns.categoryName"),
        selector: (row) => row.categoryName || "—",
        sortable: true,
        sortField: "categoryName",
        width: "160px",
      },
      {
        name: t("expenses.list.columns.amount"),
        selector: (row) => (
          <span className="fw-semibold">
            ₹{Number(row.amount || 0).toLocaleString("en-IN")}
          </span>
        ),
        sortable: true,
        sortField: "amount",
        width: "120px",
      },
      {
        name: t("expenses.list.columns.mode"),
        selector: (row) => {
          const isBank = row.paymentType === "bank";
          return (
            <span
              className={`badge ${isBank ? "bg-info" : "bg-success"} rounded-pill px-2 py-1`}
              style={{ fontSize: "11px" }}
            >
              {isBank ? t("expenses.list.paymentType.bank") : t("expenses.list.paymentType.cash")}
            </span>
          );
        },
        sortable: true,
        sortField: "paymentType",
        width: "90px",
      },
      {
        name: t("expenses.list.columns.bank"),
        selector: (row) => (row.paymentType === "bank" ? row.bankName || "—" : "—"),
        width: "250px",
      },
      {
        name: t("expenses.list.columns.chequeNo"),
        selector: (row) => (row.paymentType === "bank" ? row.chequeNumber || "—" : "—"),
        width: "140px",
      },
      {
        name: t("expenses.list.columns.date"),
        selector: (row) => formatDisplayDateOrDash(row.date, DATE_FORMAT.DISPLAY_SHORT),
        sortable: true,
        sortField: "date",
        width: "110px",
      },
      {
        name: t("expenses.list.columns.description"),
        selector: (row) => row.description || "—",
        sortable: true,
        sortField: "description",
        width: "160px",
      },
      {
        name: t("expenses.list.columns.actions"),
        width: "100px",
        center: true,
        actionItems: [
          {
            label: t("expenses.list.actions.edit"),
            icon: "ri-pencil-fill",
            className: "btn btn-outline-success",
            onClick: (data) => {
              setEditingExpenseId(data.id);
              setShowAddModal(true);
            },
          },
          {
            label: t("expenses.list.actions.delete"),
            icon: "ri-delete-bin-fill text-danger",
            className: "btn btn-outline-danger",
            isConfirm: true,
            variant: "DELETE",
            size: MODAL_SIZES.MEDIUM,
            onClick: (data) => {
              handleDelete(data.id, deleteExpense, setExpensesData, () =>
                setRefresh((r) => !r)
              );
            },
          },
        ],
      },
    ],
    // setExpensesData from useApi is stable.
    [t] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const handleRowClick = (row) => {
    setSelectedExpense(row);
    setShowDetailModal(true);
  };

  return (
    <React.Fragment>
      <Row>
        <Col lg={12}>
          <CustomTable
            columns={expensesColumns}
            data={expensesData}
            buttonAction={openAddExpenseModal}
            buttonLabel={t("expenses.list.button.addExpense")}
            buttonIcon="bx bx-plus"
            onStatusChange={(data) => setExpensesFilters(data)}
            searchKeys={["categoryName", "description"]}
            searchPlaceholder={t("expenses.list.search.placeholder")}
            onRowClicked={handleRowClick}
            pointerOnHover
            highlightOnHover
          />
        </Col>
      </Row>
      <AddExpenseModal
        show={showAddModal}
        toggle={closeExpenseModal}
        editingExpenseId={editingExpenseId}
      />
      <ExpenseDetailModal
        show={showDetailModal}
        toggle={() => setShowDetailModal(false)}
        expense={selectedExpense}
      />
    </React.Fragment>
  );
};

export default withTranslation()(List);
